import { TILL_ACTIONS, tillAction, type TillButton } from '@sp/core';
import type { IconName } from '../components/icons';
import { hasKey, tr, type Language } from '../i18n';

/**
 * The till's action registry: what each function-button action DOES on this till.
 *
 * Where a button appears is data (`TillButton`, edited in Admin); what it does is code, here. The
 * ids and the rules a layout must obey are shared with the API through `@sp/core`. Everything the
 * till can do is listed, so the search palette finds an action even when no button carries it.
 *
 * An action can be:
 *  - hidden: its feature is off for this store (No Tax not allowed, customers switched off). The
 *    button is not drawn at all, so a till with every new feature off looks exactly as before.
 *  - disabled with a reason: it exists but cannot run now (no items, no permission). The reason is
 *    shown, because a button that silently does nothing is how a cashier loses trust in the till.
 */

export interface TillActionEnv {
  lineCount: number;
  hasCustomer: boolean;
  heldCount: number;
  shiftOpen: boolean;
  canBill: boolean;
  hasLastReceipt: boolean;
  /** Server and store switches. */
  taxRemovalAllowed: boolean;
  canRemoveTax: boolean;
  taxExempt: boolean;
  customerEnabled: boolean;
  /** Open items: the store switch, the user's permission, and whether the bill has one to tax. */
  openItemEnabled?: boolean;
  canOpenItem?: boolean;
  hasOpenItemLines?: boolean;
  /** Features the API advertises; a till newer than its API hides what the API would ignore. */
  features: readonly string[];
}

export interface TillActionHandlers {
  find: () => void;
  attachCustomer: () => void;
  removeCustomer: () => void;
  editQuantity: () => void;
  removeLast: () => void;
  hold: () => void;
  resume: () => void;
  discount: () => void;
  discountPreset: (percentBps: number) => void;
  pay: () => void;
  reprint: () => void;
  newBill: () => void;
  toggleNoTax: () => void;
  customerDisplay: () => void;
  openItem?: () => void;
  itemTax?: () => void;
}

export interface ResolvedAction {
  id: string;
  label: string;
  icon: IconName;
  hidden: boolean;
  /** Null when it can run; otherwise why not, in words for the cashier. */
  disabledReason: string | null;
  pressed?: boolean;
  run: (params: Record<string, unknown> | null) => void;
}

const ICONS: Record<string, IconName> = {
  'nav.find': 'search',
  'customer.attach': 'user',
  'customer.lookup': 'user',
  'customer.remove': 'close',
  'line.quantity': 'scale',
  'line.removeLast': 'trash',
  'bill.hold': 'pause',
  'bill.resume': 'inbox',
  'bill.discount': 'tag',
  'bill.discountPreset': 'tag',
  'payment.take': 'receipt',
  'bill.reprint': 'printer',
  'bill.new': 'plus',
  'bill.noTax': 'alert',
  'system.customerDisplay': 'monitor',
  'line.openItem': 'plus',
  'line.itemTax': 'tag',
};

function percentLabel(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}

/**
 * The actions this build can actually run.
 *
 * `TILL_ACTIONS` is the shared vocabulary: what the API will store and validate. This is the
 * smaller set the till in *this* bundle implements. The same bundle edits the layout in Admin and
 * draws the bar on the till, so offering anything else saves a button that never appears.
 * `actions.test.ts` keeps the two in step: every id here resolves, and nothing outside it does.
 */
export const IMPLEMENTED_ACTION_IDS: readonly string[] = [
  'nav.find',
  'customer.attach',
  'customer.lookup',
  'customer.remove',
  'line.quantity',
  'line.removeLast',
  'bill.hold',
  'bill.resume',
  'bill.discount',
  'bill.discountPreset',
  'payment.take',
  'bill.reprint',
  'bill.new',
  'bill.noTax',
  'system.customerDisplay',
  'line.openItem',
  'line.itemTax',
];

export function isActionImplemented(actionId: string): boolean {
  return IMPLEMENTED_ACTION_IDS.includes(actionId);
}

/** Resolve one action for the current till state. Unknown ids resolve to null (and are skipped). */
export function resolveAction(
  actionId: string,
  params: Record<string, unknown> | null,
  env: TillActionEnv,
  handlers: TillActionHandlers,
): ResolvedAction | null {
  const spec = tillAction(actionId);
  if (!spec) return null;
  const labelKey = `action.${actionId}`;
  const base = { id: actionId, label: hasKey(labelKey) ? tr(labelKey) : spec.label, icon: ICONS[actionId] ?? 'menu', hidden: false, disabledReason: null as string | null };
  const needsItems = env.lineCount === 0 ? tr('action.why.needsItems') : null;
  const needsShift = env.shiftOpen ? null : tr('action.why.needsShift');

  switch (actionId) {
    case 'nav.find':
      return { ...base, run: handlers.find };
    case 'customer.attach':
    case 'customer.lookup':
      return { ...base, hidden: !env.customerEnabled, run: handlers.attachCustomer };
    case 'customer.remove':
      return {
        ...base,
        hidden: !env.customerEnabled,
        disabledReason: env.hasCustomer ? null : tr('action.why.noCustomer'),
        run: handlers.removeCustomer,
      };
    case 'line.quantity':
      return { ...base, disabledReason: needsItems, run: handlers.editQuantity };
    case 'line.removeLast':
      return { ...base, disabledReason: needsItems, run: handlers.removeLast };
    case 'bill.hold':
      return { ...base, disabledReason: needsItems, run: handlers.hold };
    case 'bill.resume':
      return {
        ...base,
        disabledReason: env.lineCount > 0 ? tr('action.why.finishFirst') : env.heldCount === 0 ? tr('action.why.noneHeld') : null,
        run: handlers.resume,
      };
    case 'bill.discount':
      return { ...base, disabledReason: needsItems, run: handlers.discount };
    case 'bill.discountPreset': {
      const bps = typeof params?.percentBps === 'number' ? params.percentBps : 0;
      return {
        ...base,
        label: bps > 0 ? tr('action.bill.discountPreset', { percent: percentLabel(bps) }) : base.label,
        disabledReason: bps > 0 ? needsItems : tr('action.why.noPercent'),
        run: () => handlers.discountPreset(bps),
      };
    }
    case 'payment.take':
      return {
        ...base,
        disabledReason: needsShift ?? needsItems ?? (env.canBill ? null : tr('action.why.cannotPay')),
        run: handlers.pay,
      };
    case 'bill.reprint':
      return { ...base, disabledReason: env.hasLastReceipt ? null : tr('action.why.noReceipt'), run: handlers.reprint };
    case 'bill.new':
      return { ...base, run: handlers.newBill };
    case 'bill.noTax':
      return {
        ...base,
        hidden: !env.taxRemovalAllowed || !env.features.includes('taxExempt'),
        disabledReason: !env.canRemoveTax ? tr('action.why.managerTax') : needsItems,
        pressed: env.taxExempt,
        run: handlers.toggleNoTax,
      };
    case 'system.customerDisplay':
      return { ...base, run: handlers.customerDisplay };
    case 'line.openItem':
      return {
        ...base,
        hidden: !env.openItemEnabled || !env.features.includes('openItem'),
        disabledReason: needsShift ?? (env.canOpenItem ? null : tr('action.why.managerOpenItem')),
        run: () => handlers.openItem?.(),
      };
    case 'line.itemTax':
      return {
        ...base,
        hidden: !env.openItemEnabled || !env.features.includes('openItem'),
        // No Tax wins: the whole bill is already without tax.
        disabledReason: env.taxExempt
          ? tr('action.why.noTaxOn')
          : env.hasOpenItemLines
            ? null
            : tr('action.why.needsOpenItem'),
        run: () => handlers.itemTax?.(),
      };
    default:
      return null;
  }
}

export interface PlacedButton {
  button: TillButton;
  action: ResolvedAction;
  label: string;
}

/** The buttons to draw: visible, known, and not hidden by a switched-off feature. */
export function placeButtons(
  buttons: readonly TillButton[],
  env: TillActionEnv,
  handlers: TillActionHandlers,
  language: Language = 'en',
): PlacedButton[] {
  const placed: PlacedButton[] = [];
  for (const button of buttons) {
    if (!button.visible) continue;
    // A layout saved by a newer version may name an action this till does not know: skip it.
    const action = resolveAction(button.actionId, button.params, env, handlers);
    if (!action || action.hidden) continue;
    const override = button.label?.[language] ?? button.label?.en;
    placed.push({ button, action, label: override?.trim() ? override.trim() : action.label });
  }
  return placed.sort((a, b) => a.button.page - b.button.page || a.button.position - b.button.position);
}

/** Every action, for the search palette, whether or not a button carries it. Legacy ones already have entries. */
export function paletteActions(env: TillActionEnv, handlers: TillActionHandlers): ResolvedAction[] {
  return TILL_ACTIONS.filter((spec) => !spec.legacy && !spec.params)
    .map((spec) => resolveAction(spec.id, null, env, handlers))
    .filter((action): action is ResolvedAction => action !== null && !action.hidden);
}
